import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { 
  Trophy, 
  Clock, 
  Gift, 
  Loader2, 
  Lock, 
  CheckCircle2,
  XCircle,
  Sparkles,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUZZLE_IMAGE = "https://static.prod-images.emergentagent.com/jobs/8dcb2a80-f509-441b-b99e-0f9224a5cf02/images/07d3ac863d3a88c5ef6c5eaac6afaece4e21561b363c893e9e724a07c8bc39db.png";
const AIRPODS_IMAGE = "https://images.unsplash.com/photo-1606741965429-8d76ff50bb2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxhcHBsZSUyMGFpcnBvZHMlMjBwcm98ZW58MHx8fHwxNzc0NzA3MTMxfDA&ixlib=rb-4.1.0&q=85&w=400";

export default function Puzzle() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [puzzle, setPuzzle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const fetchPuzzle = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/puzzle/current`, { withCredentials: true });
      setPuzzle(response.data);
    } catch (error) {
      // Puzzle fetch failed
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user) {
      fetchPuzzle();
    }
  }, [user, authLoading, fetchPuzzle]);

  useEffect(() => {
    if (puzzle && !puzzle.is_open) {
      const timer = setInterval(() => {
        const now = new Date();
        const opensAt = new Date(puzzle.opens_at);
        const diff = opensAt - now;

        if (diff <= 0) {
          fetchPuzzle();
          clearInterval(timer);
        } else {
          setCountdown({
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((diff % (1000 * 60)) / 1000)
          });
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [puzzle, fetchPuzzle]);

  const handleSubmit = async () => {
    if (!selectedAnswer) {
      toast.error('Please select an answer');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/puzzle/submit`, {
        answer: selectedAnswer
      }, { withCredentials: true });
      
      setResult(response.data);
      if (response.data.is_winner) {
        toast.success('Congratulations! You won!');
      }
      fetchPuzzle();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF6F0]" data-testid="puzzle-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#E05D3A]" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center py-12 px-4" data-testid="puzzle-login-prompt">
        <Card className="max-w-md text-center">
          <CardContent className="pt-6">
            <Lock className="w-12 h-12 text-[#4A4A4A] mx-auto mb-4" />
            <h2 className="font-serif text-xl font-bold mb-2 text-[#2C402E]">Login Required</h2>
            <p className="text-[#4A4A4A] mb-4">Please login to participate in the monthly puzzle</p>
            <Button onClick={() => navigate('/login')} className="bg-[#E05D3A] hover:bg-[#C94F2E]">
              Login to Continue
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2C402E]" data-testid="puzzle-page">
      {/* Header */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-[#D4AF37] text-[#2C402E] mb-4 rounded-full px-4 py-1">Monthly Contest</Badge>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="puzzle-title">
            Win {puzzle?.prize || 'Amazing Prizes'}!
          </h1>
          <p className="text-[#FAF6F0]/80 text-lg max-w-2xl mx-auto">
            Solve the puzzle and be the first to answer correctly. Opens every 15th at <span className="font-bold text-[#D4AF37]">15:15:15</span> UK time!
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Puzzle Card */}
          <Card className="overflow-hidden" data-testid="puzzle-card">
            <CardHeader className="bg-gradient-to-r from-[#E05D3A] to-[#C94F2E] text-white">
              <CardTitle className="font-serif flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                {puzzle?.is_open ? "Today's Puzzle" : 'Coming Soon'}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {puzzle?.has_winner ? (
                <div className="text-center py-8">
                  <Trophy className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-bold mb-2 text-[#2C402E]">We Have a Winner!</h3>
                  <p className="text-[#4A4A4A] mb-4">
                    Congratulations to <span className="font-semibold text-[#E05D3A]">{puzzle.winner_name}</span>!
                  </p>
                  <Badge className="bg-[#D4AF37]/10 text-[#D4AF37]">
                    Won Apple AirPods
                  </Badge>
                  {puzzle.user_submitted && !puzzle.user_won && (
                    <p className="text-[#4A4A4A] text-sm mt-4">
                      Better luck next month!
                    </p>
                  )}
                </div>
              ) : puzzle?.user_submitted ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-16 h-16 text-[#2C402E] mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-bold mb-2 text-[#2C402E]">Answer Submitted!</h3>
                  <p className="text-[#4A4A4A]">
                    Your answer has been recorded. Check back later for results!
                  </p>
                </div>
              ) : puzzle?.is_open ? (
                <div className="space-y-6">
                  <div className="bg-[#FAF6F0] rounded-xl p-4">
                    <p className="text-sm text-[#E05D3A] font-medium mb-2">
                      {puzzle.puzzle_type.toUpperCase()} PUZZLE
                    </p>
                    <p className="text-lg font-semibold text-[#2C402E]">
                      {puzzle.question}
                    </p>
                  </div>

                  <RadioGroup value={selectedAnswer} onValueChange={setSelectedAnswer}>
                    <div className="space-y-3">
                      {puzzle.options?.map((option, index) => (
                        <Label
                          key={index}
                          htmlFor={`option-${index}`}
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            selectedAnswer === option
                              ? 'border-[#E05D3A] bg-[#E05D3A]/5'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <RadioGroupItem 
                            value={option} 
                            id={`option-${index}`}
                            data-testid={`puzzle-option-${index}`}
                          />
                          <span className="font-medium text-[#2C402E]">{option}</span>
                        </Label>
                      ))}
                    </div>
                  </RadioGroup>

                  {result && (
                    <div className={`p-4 rounded-xl ${
                      result.is_winner 
                        ? 'bg-[#D4AF37]/10 text-[#D4AF37]' 
                        : result.is_correct 
                          ? 'bg-[#2C402E]/10 text-[#2C402E]'
                          : 'bg-red-100 text-red-800'
                    }`}>
                      <div className="flex items-center gap-2">
                        {result.is_winner ? (
                          <Trophy className="w-5 h-5" />
                        ) : result.is_correct ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <XCircle className="w-5 h-5" />
                        )}
                        <span className="font-medium">{result.message}</span>
                      </div>
                    </div>
                  )}

                  <Button
                    className="w-full bg-[#E05D3A] hover:bg-[#C94F2E]"
                    onClick={handleSubmit}
                    disabled={submitting || !selectedAnswer}
                    data-testid="puzzle-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Answer'
                    )}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Lock className="w-12 h-12 text-[#4A4A4A] mx-auto mb-4" />
                  <h3 className="font-serif text-xl font-bold mb-4 text-[#2C402E]">Puzzle Opens In</h3>
                  <div className="flex justify-center gap-4 mb-6">
                    <div className="bg-[#FAF6F0] rounded-xl p-4 min-w-[70px]">
                      <div className="text-3xl font-bold text-[#E05D3A]">{countdown.days}</div>
                      <div className="text-xs text-[#4A4A4A] uppercase">Days</div>
                    </div>
                    <div className="bg-[#FAF6F0] rounded-xl p-4 min-w-[70px]">
                      <div className="text-3xl font-bold text-[#E05D3A]">{countdown.hours}</div>
                      <div className="text-xs text-[#4A4A4A] uppercase">Hours</div>
                    </div>
                    <div className="bg-[#FAF6F0] rounded-xl p-4 min-w-[70px]">
                      <div className="text-3xl font-bold text-[#E05D3A]">{countdown.minutes}</div>
                      <div className="text-xs text-[#4A4A4A] uppercase">Mins</div>
                    </div>
                    <div className="bg-[#FAF6F0] rounded-xl p-4 min-w-[70px]">
                      <div className="text-3xl font-bold text-[#E05D3A]">{countdown.seconds}</div>
                      <div className="text-xs text-[#4A4A4A] uppercase">Secs</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-[#4A4A4A]">
                    <Calendar className="w-4 h-4" />
                    <span>15th of every month at 15:15:15 UK time</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Prize Card */}
          <div className="space-y-6">
            <Card className="overflow-hidden bg-gradient-to-br from-[#E05D3A] to-[#C94F2E] text-white">
              <CardContent className="p-8">
                <div className="flex items-center gap-2 mb-4">
                  <Gift className="w-6 h-6 text-[#D4AF37]" />
                  <span className="font-semibold">This Month's Prize</span>
                </div>
                <h3 className="font-serif text-3xl font-bold mb-4">{puzzle?.prize || 'Amazing Prize'}</h3>
                <p className="text-white/80 text-sm">
                  Be the first to solve the puzzle correctly and win!
                </p>
              </CardContent>
            </Card>

            {/* Prize Schedule */}
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2 text-[#2C402E]">
                  <Trophy className="w-5 h-5 text-[#D4AF37]" />
                  Prize Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {puzzle?.prize_schedule?.map((item) => (
                  <div key={item.month} className="flex items-center justify-between py-2 border-b border-[#2C402E]/10 last:border-0">
                    <span className="text-sm text-[#4A4A4A]">{item.month}</span>
                    <span className="font-semibold text-[#2C402E] text-sm">{item.prize}</span>
                  </div>
                ))}
                <p className="text-xs text-[#4A4A4A] italic mt-2">Prizes rotate every 3 months</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2 text-[#2C402E]">
                  <Clock className="w-5 h-5 text-[#E05D3A]" />
                  How It Works
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#E05D3A]/10 flex items-center justify-center flex-shrink-0 font-bold text-[#E05D3A]">
                    1
                  </div>
                  <div>
                    <p className="font-medium text-[#2C402E]">Wait for the puzzle</p>
                    <p className="text-sm text-[#4A4A4A]">Opens on 15th at 15:15:15 UK time</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#E05D3A]/10 flex items-center justify-center flex-shrink-0 font-bold text-[#E05D3A]">
                    2
                  </div>
                  <div>
                    <p className="font-medium text-[#2C402E]">Solve the puzzle</p>
                    <p className="text-sm text-[#4A4A4A]">Select your answer quickly</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#E05D3A]/10 flex items-center justify-center flex-shrink-0 font-bold text-[#E05D3A]">
                    3
                  </div>
                  <div>
                    <p className="font-medium text-[#2C402E]">First correct wins!</p>
                    <p className="text-sm text-[#4A4A4A]">Speed matters - be the fastest</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="relative">
              <img 
                src={PUZZLE_IMAGE}
                alt="Puzzle"
                className="w-full h-48 object-contain animate-float"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
